import { useCallback, useEffect, useRef, useState } from 'react';
import type { AddressInputParams } from 'fe-be-utils';
import { Loader2, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { API_BASE_URL } from '../../constants';
import { getBackgroundStyle } from '../../utils/theme';

type AddressInputComponentProps = {
  params: AddressInputParams;
  onSubmit: (value: string) => void;
  onDismiss: () => void;
  accentColor?: string;
  textOverAccentColor?: string;
  chatId?: string;
};

const DEBOUNCE_MS = 300;

export const AddressInputComponent = ({
  params,
  onSubmit,
  onDismiss,
  accentColor,
  textOverAccentColor,
  chatId
}: AddressInputComponentProps) => {
  const [address, setAddress] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController>();

  const fetchSuggestions = useCallback(
    async (query: string) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsLoading(true);
      try {
        const searchParams = new URLSearchParams({ text: query });
        if (params.countryRestriction) {
          searchParams.set('countryCode', params.countryRestriction);
        }
        if (chatId) {
          searchParams.set('chatId', chatId);
        }

        const res = await fetch(
          `${API_BASE_URL}/app/public/widget/autocomplete-address?${searchParams.toString()}`,
          { signal: controller.signal }
        );
        const data = (await res.json()) as { suggestions: string[] };

        setSuggestions(data.suggestions || []);
        setShowSuggestions((data.suggestions || []).length > 0);
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setIsLoading(false);
      }
    },
    [params.countryRestriction, chatId]
  );

  const handleInputChange = (value: string) => {
    setAddress(value);
    clearTimeout(debounceRef.current);

    if (value.trim().length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value.trim());
    }, DEBOUNCE_MS);
  };

  const handleSelectSuggestion = (suggestion: string) => {
    setAddress(suggestion);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && address.trim()) {
      setShowSuggestions(false);
      onSubmit(address.trim());
    }
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, []);

  return (
    <div className="mx-3 mb-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
      <p className="mb-3 whitespace-pre-line text-sm text-gray-700 dark:text-gray-300">{params.prompt}</p>

      <div ref={containerRef} className="relative">
        <div className="relative">
          <input
            type="text"
            placeholder="Start typing an address..."
            value={address}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            className={cn(
              'w-full rounded-lg border border-gray-200 bg-gray-50 py-2.5 pl-9 pr-3 text-sm text-gray-900',
              'placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500',
              'dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500'
            )}
          />
          <div className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
          </div>
        </div>

        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
            {suggestions.map((suggestion, i) => (
              <button
                key={i}
                type="button"
                onClick={() => handleSelectSuggestion(suggestion)}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700',
                  i === 0 && 'rounded-t-lg',
                  i === suggestions.length - 1 && 'rounded-b-lg'
                )}
              >
                <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                <span className="truncate">{suggestion}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-3 flex gap-2">
        <Button
          size="sm"
          onClick={() => onSubmit(address.trim())}
          disabled={!address.trim()}
          style={
            accentColor
              ? { ...getBackgroundStyle(accentColor), color: textOverAccentColor ?? '#ffffff' }
              : undefined
          }
        >
          Confirm
        </Button>
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Skip
        </Button>
      </div>
    </div>
  );
};
